
import React, { useState } from 'react';
import './LanScanner.css';

const LanScanner = () => {
  const [subnet, setSubnet] = useState('10.100.93');
  const [hosts, setHosts] = useState([]);
  const [scanning, setScanning] = useState(false);

  const startScan = async () => {
    setHosts([]);
    setScanning(true);

    try {
      const response = await fetch('/diagnostic-tools/lan-scan-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subnet })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const hostData = JSON.parse(line.replace('data: ', ''));
            setHosts((prev) => [...prev, hostData]);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }

    setScanning(false);
  };

  const averageLatency = hosts.length
    ? (hosts.reduce((sum, h) => sum + Number(h.latency_ms || 0), 0) / hosts.length).toFixed(2)
    : '0.00';

  return (
    <div className="tool-container premium-lan-scanner">

      {/* HEADER */}

      <div className="lan-header">
        <div className="lan-heading">
          <div className="lan-icon">📡</div>
          <div>
            <h4>Live LAN Scanner</h4>
            <p>Network Discovery & Device Monitoring</p>
          </div>
        </div>

        <span className={`lan-status ${scanning ? 'scanning' : 'idle'}`}>
          <span></span>
          {scanning ? 'SCANNING' : 'NETWORK SCANNER'}
        </span>
      </div>

      {/* SEARCH */}

      <div className="lan-search-section">

        <label>NETWORK SUBNET</label>

        <div className="input-group">

          <input
            type="text"
            className="form-control"
            value={subnet}
            onChange={(e) => setSubnet(e.target.value)}
            placeholder="e.g. 10.100.93"
          />

          <button
            className="btn"
            onClick={startScan}
            disabled={scanning || !subnet.trim()}
          >
            {scanning ? (
              <>
                <span className="lan-spinner"></span>
                Scanning...
              </>
            ) : (
              <>▶ Start Scan</>
            )}
          </button>

        </div>

        <small>Example: 10.100.93 · 192.168.1</small>
      </div>

      {/* STATISTICS */}

      <div className="lan-stats">

        <div className="lan-stat-card">
          <div className="lan-stat-icon">💻</div>
          <span>DISCOVERED HOSTS</span>
          <strong>{hosts.length}</strong>
          <small>Online devices found</small>
        </div>

        <div className="lan-stat-card">
          <div className="lan-stat-icon">⚡</div>
          <span>AVERAGE LATENCY</span>
          <strong>{averageLatency} <small>ms</small></strong>
          <small>Response time average</small>
        </div>

        <div className="lan-stat-card">
          <div className="lan-stat-icon">🔍</div>
          <span>SCAN STATUS</span>
          <strong className={scanning ? 'lan-active-text' : 'lan-idle-text'}>
            {scanning ? 'Active' : hosts.length ? 'Completed' : 'Ready'}
          </strong>
          <small>{scanning ? 'Receiving live results' : 'Waiting for scan'}</small>
        </div>

      </div>

      {/* LIVE RESULT TABLE */}

      <div className="lan-results">

        <div className="lan-table-header">
          <div>
            <h5>Discovered Devices</h5>
            <p>Live network host information</p>
          </div>

          <span className="lan-host-count">
            {hosts.length} HOSTS
          </span>
        </div>

        <div className="table-responsive lan-table-wrapper">

          <table className="table lan-table">

            <thead>
              <tr>
                <th>#</th>
                <th>IP ADDRESS</th>
                <th>MAC ADDRESS</th>
                <th>LATENCY</th>
                <th>STATUS</th>
              </tr>
            </thead>

            <tbody>

              {hosts.length > 0 ? (
                hosts.map((h, index) => (
                  <tr key={`${h.ip}-${index}`}>

                    <td>
                      <span className="lan-row-number">
                        {index + 1}
                      </span>
                    </td>

                    <td>
                      <div className="lan-ip-cell">
                        <span className="lan-device-icon">🖥️</span>
                        <strong>{h.ip}</strong>
                      </div>
                    </td>

                    <td>
                      <span className="lan-mac">
                        {h.mac || 'N/A'}
                      </span>
                    </td>

                    <td>
                      <span className={`lan-latency ${
                        Number(h.latency_ms) > 20 ? 'latency-high' :
                        Number(h.latency_ms) > 5 ? 'latency-medium' :
                        'latency-low'
                      }`}>
                        {h.latency_ms} ms
                      </span>
                    </td>

                    <td>
                      <span className="lan-online">
                        <span></span>
                        ONLINE
                      </span>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">
                    <div className="lan-empty">
                      <div>📡</div>
                      <strong>
                        {scanning ? 'Discovering Network Devices...' : 'No Devices Found'}
                      </strong>
                      <p>
                        {scanning
                          ? 'Waiting for live scanner results.'
                          : 'Enter a subnet and start scanning your LAN.'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

      </div>

      {/* FOOTER */}

      <div className="lan-footer">
        <span>LAN NETWORK DISCOVERY</span>
        <span>LIVE SCANNER</span>
      </div>

    </div>
  );
};

export default LanScanner;
